import React from 'react';
import { X, AlertTriangle, Zap } from 'lucide-react';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  current: number;
  limit: number;
  onUpgrade?: () => void;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, current, limit, onUpgrade }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <AlertTriangle size={22} />
            </div>
            <h3 className="text-lg font-bold">Tool limit reached</h3>
          </div>
          <p className="text-white/90 text-sm">
            You've reached {current}/{limit} tools on your current plan.
          </p>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-slate-600 text-sm leading-relaxed">
            Upgrade your plan to add more tools and unlock advanced features like SaaS Discovery, multi-level approval workflows, and extended audit history.
          </p>

          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Current usage</span>
              <span className="font-bold text-slate-800">{current} / {limit} tools</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full transition-all"
                style={{ width: `${Math.min(100, (current / limit) * 100)}%` }}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-medium text-sm transition-colors"
            >
              Maybe later
            </button>
            <button
              onClick={() => {
                onClose();
                if (onUpgrade) onUpgrade();
              }}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium text-sm transition-colors flex items-center justify-center gap-2"
            >
              <Zap size={16} />
              Upgrade plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
